import styled, { keyframes } from "styled-components";
import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
  ANIMATION,
} from "../../../utils/constants";
import { BaseButton } from "../../../components/Button";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};
`;

export const HeroWrapper = styled.div`
  padding: ${SPACING.md}px ${SPACING.md}px;

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardPadding}px ${SPACING.md}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  }
`;

export const ChartsRow = styled.div`
  display: flex;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  padding-top: 0px;

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.chartMargin}px;
    padding-top: 0px;
  }

  @media not all and ${QUERIES.laptopAndUp} {
    flex-direction: column;
  }
`;

export const TransactionsRow = styled.div`
  display: flex;
  padding: ${SPACING.md}px;
  padding-top: 0px;

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.chartMargin}px;
    padding-top: 0px;
  }
`;

export const CashbackRow = styled.div`
  display: flex;
  padding: ${SPACING.md}px;
  padding-top: 0px;

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.chartMargin}px;
    padding-top: 0px;
  }

  &:empty {
    display: none;
  }
`;

export const RecurringRow = styled.div`
  display: flex;
  padding: ${SPACING.md}px;
  padding-top: 0px;

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.chartMargin}px;
    padding-top: 0px;
  }
`;

export const SectionCard = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--color-card-background);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: ${SPACING.lg}px;
  box-shadow: var(--shadow-small);
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${SPACING.sm}px;
  margin-bottom: ${SPACING.md}px;
  flex-wrap: wrap;
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  color: var(--color-text-default);
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-lg);
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

export const SubtitleText = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  line-height: 1.4;
`;

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  flex-shrink: 0;

  > :last-child {
    min-width: 90px;
  }
`;

export const ChartEmptyState = styled.div`
  padding: ${SPACING.md + SPACING.xs}px 0 ${SPACING.xs}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

export const ChartErrorState = styled.div`
  padding: ${SPACING.md + SPACING.xs}px 0 ${SPACING.xs}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.sm}px;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  text-align: center;
`;

export const ToggleBtn = styled(BaseButton)<{ $active: boolean }>`
  justify-content: center;
  padding: ${SPACING.xs}px ${SPACING.sm + SPACING.xs + SPACING.xxs}px;
  height: 32px;
  border-radius: 8px;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: ${({ $active }) => ($active ? "600" : "500")};
  cursor: pointer;
  transition: ${ANIMATION.transitions.all};
  background: ${({ $active }) =>
    $active ? "var(--color-primary)" : "transparent"};
  color: ${({ $active }) =>
    $active ? "var(--color-white)" : "var(--color-text-secondary)"};
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--color-primary)" : "var(--color-border)"};
  white-space: nowrap;

  &:hover {
    background: ${({ $active }) =>
      $active
        ? "var(--color-primary-hover)"
        : "var(--color-toggle-hover-background)"};
    border-color: ${({ $active }) =>
      $active ? "var(--color-primary-hover)" : "var(--color-primary)"};
    color: ${({ $active }) =>
      $active ? "var(--color-white)" : "var(--color-text-default)"};
  }

  &:active {
    transform: scale(0.98);
  }

  @media not all and ${QUERIES.tabletAndUp} {
    height: auto;
    min-height: 44px;
  }
`;
